import React, { useState } from 'react';
import axios from 'axios';
import config from '../config/config';
import { useToast } from './ToastContext';
import { FaHdd, FaEdit, FaTrash, FaChevronDown, FaChevronUp } from 'react-icons/fa';

// Cadastro dos planos de armazenamento das empresas que acessam via web (só MANAGER).
// A lista vem do pai (CadastroEmpresa), que também usa os planos no formulário da empresa.
const planoVazio = { nome: '', capacidadeGb: '', valorMensal: '', ativo: true };

export const formatarMoeda = (valor) =>
  valor === null || valor === undefined || valor === ''
    ? '—'
    : Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function PlanosArmazenamento({ planos, onAlterado }) {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();
  const [aberto, setAberto] = useState(false);
  const [form, setForm] = useState(planoVazio);
  const [editandoId, setEditandoId] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

  const limpar = () => {
    setForm(planoVazio);
    setEditandoId(null);
  };

  const salvar = async (e) => {
    e.preventDefault();
    const capacidade = parseInt(form.capacidadeGb, 10);
    if (!form.nome.trim()) return addToast('error', 'Informe o nome do plano.');
    if (!capacidade || capacidade <= 0) return addToast('error', 'A capacidade deve ser maior que zero.');

    const dados = {
      nome: form.nome.trim(),
      capacidadeGb: capacidade,
      valorMensal: form.valorMensal === '' ? null : Number(String(form.valorMensal).replace(',', '.')),
      ativo: form.ativo
    };

    setSalvando(true);
    try {
      if (editandoId) {
        await axios.put(`${API_BASE_URL}/planos-armazenamento/${editandoId}`, dados, authHeader());
      } else {
        await axios.post(`${API_BASE_URL}/planos-armazenamento`, dados, authHeader());
      }
      addToast('success', editandoId ? 'Plano atualizado!' : 'Plano cadastrado!');
      limpar();
      onAlterado();
    } catch (error) {
      addToast('error', typeof error.response?.data === 'string' ? error.response.data : 'Erro ao salvar plano.');
    } finally {
      setSalvando(false);
    }
  };

  const editar = (plano) => {
    setEditandoId(plano.id);
    setForm({
      nome: plano.nome || '',
      capacidadeGb: plano.capacidadeGb ?? '',
      valorMensal: plano.valorMensal ?? '',
      ativo: plano.ativo !== false
    });
  };

  const excluir = async (plano) => {
    if (!window.confirm(`Excluir o plano "${plano.nome}"?`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/planos-armazenamento/${plano.id}`, authHeader());
      addToast('success', 'Plano excluído!');
      if (editandoId === plano.id) limpar();
      onAlterado();
    } catch (error) {
      addToast('error', typeof error.response?.data === 'string' ? error.response.data : 'Erro ao excluir plano.');
    }
  };

  return (
    <div style={styles.section}>
      <button type="button" style={styles.toggle} onClick={() => setAberto(!aberto)}>
        <span style={styles.toggleTitle}>
          <FaHdd /> Planos de armazenamento (acesso web)
          <span style={styles.count}>{planos.length} plano(s)</span>
        </span>
        {aberto ? <FaChevronUp /> : <FaChevronDown />}
      </button>

      {aberto && (
        <div style={styles.body}>
          <form onSubmit={salvar} style={styles.form}>
            <div style={{ ...styles.field, flex: '2 1 200px' }}>
              <label style={styles.label}>Nome *</label>
              <input style={styles.input} value={form.nome} placeholder="Ex.: Básico"
                onChange={(e) => setForm({ ...form, nome: e.target.value })} disabled={salvando} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Capacidade (GB) *</label>
              <input style={styles.input} type="number" min="1" value={form.capacidadeGb} placeholder="10"
                onChange={(e) => setForm({ ...form, capacidadeGb: e.target.value })} disabled={salvando} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Valor mensal (R$)</label>
              <input style={styles.input} type="number" min="0" step="0.01" value={form.valorMensal} placeholder="49,90"
                onChange={(e) => setForm({ ...form, valorMensal: e.target.value })} disabled={salvando} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Situação</label>
              <select style={styles.input} value={form.ativo}
                onChange={(e) => setForm({ ...form, ativo: e.target.value === 'true' })} disabled={salvando}>
                <option value={true}>Ativo</option>
                <option value={false}>Inativo</option>
              </select>
            </div>
            <div style={styles.actions}>
              <button type="submit" style={{ ...styles.button, backgroundColor: editandoId ? '#2196F3' : '#4CAF50' }} disabled={salvando}>
                {salvando ? 'Salvando...' : editandoId ? 'Atualizar' : 'Adicionar'}
              </button>
              {editandoId && (
                <button type="button" style={{ ...styles.button, backgroundColor: '#6c757d' }} onClick={limpar} disabled={salvando}>
                  Cancelar
                </button>
              )}
            </div>
          </form>

          {planos.length === 0 ? (
            <p style={styles.empty}>Nenhum plano cadastrado. Cadastre ao menos um para vincular às empresas web.</p>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Plano</th>
                  <th style={styles.th}>Capacidade</th>
                  <th style={styles.th}>Valor mensal</th>
                  <th style={styles.th}>Situação</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {planos.map((plano) => (
                  <tr key={plano.id} style={plano.ativo === false ? { opacity: 0.55 } : undefined}>
                    <td style={styles.td}>{plano.nome}</td>
                    <td style={styles.td}>{plano.capacidadeGb} GB</td>
                    <td style={styles.td}>{formatarMoeda(plano.valorMensal)}</td>
                    <td style={styles.td}>{plano.ativo === false ? 'Inativo' : 'Ativo'}</td>
                    <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button type="button" style={styles.iconButton} title="Editar plano" onClick={() => editar(plano)}><FaEdit /></button>
                      <button type="button" style={{ ...styles.iconButton, color: '#dc3545' }} title="Excluir plano" onClick={() => excluir(plano)}><FaTrash /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p style={styles.hint}>Plano em uso não pode ser excluído: desative-o para que não seja oferecido a novas empresas.</p>
        </div>
      )}
    </div>
  );
}

const styles = {
  section: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    marginBottom: '30px',
    overflow: 'hidden',
  },
  toggle: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '18px 25px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    color: '#333',
  },
  toggleTitle: { display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600', flexWrap: 'wrap' },
  count: { fontSize: '13px', fontWeight: 'normal', color: '#666' },
  body: { padding: '0 25px 25px' },
  form: { display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'flex-end', marginBottom: '20px' },
  field: { flex: '1 1 140px', minWidth: '140px' },
  label: { display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333', fontSize: '13px' },
  input: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '2px solid #e9ecef',
    borderRadius: '6px',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
  },
  actions: { display: 'flex', gap: '10px' },
  button: {
    padding: '11px 20px',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { textAlign: 'left', padding: '10px', borderBottom: '2px solid #e9ecef', color: '#666', fontSize: '12px', textTransform: 'uppercase' },
  td: { padding: '10px', borderBottom: '1px solid #f1f3f5', color: '#333' },
  iconButton: { background: 'none', border: 'none', cursor: 'pointer', color: '#2196F3', fontSize: '15px', padding: '4px 8px' },
  empty: { color: '#666', fontSize: '14px', margin: '10px 0' },
  hint: { color: '#666', fontSize: '12px', fontStyle: 'italic', marginTop: '12px', marginBottom: 0 },
};

export default PlanosArmazenamento;
